 /**
  * CR307 D1-D2: Effective Config Resolution
  * 
  * Implements the canonical layering model for Template Editor:
  * 1. draftOverrides (preview-only, not persisted)
  * 2. overrides_json (persisted V2 overrides)
  * 3. template_json (deal-type baseline)
  * 4. variant template_json (base structure)
  * 
  * Uses deep merge semantics throughout.
  */
 
 import { deepMerge, deepMerge3, sanitizeOverrides, type ValueSource, traceValueSource } from "./deepMerge";
 
 // ================================================================
 // TYPES
 // ================================================================
 
 export interface EffectiveConfigContext {
   /** Variant base template (from deal_card_template_variants) */
   variantTemplate?: Record<string, unknown> | null;
   /** Deal-type template (from deal_card_templates.template_json) */
   template?: Record<string, unknown> | null;
   /** Persisted overrides (from deal_card_templates.overrides_json) */
   overrides?: Record<string, unknown> | null;
   /** Legacy template for migration (from deal_card_templates.legacy_template_json) */
   legacy?: Record<string, unknown> | null;
   /** Draft overrides (in-memory, highest precedence) */
   draft?: Record<string, unknown> | null;
 }
 
 export interface EffectiveConfigResult<T> {
   /** The fully resolved effective config */
   effective: T;
   /** Whether any draft changes are present */
   hasDraft: boolean;
   /** Source tracking for each top-level key */
   sources: Record<string, ValueSource>;
 }
 
 // ================================================================
 // RESOLUTION
 // ================================================================
 
 /**
  * Resolve effective config using canonical layering:
  * draft > overrides > template > variant > legacy
  * 
  * Uses proper deep merge semantics at each layer.
  */
 export function resolveEffectiveConfig<T extends Record<string, unknown>>(
   ctx: EffectiveConfigContext
 ): EffectiveConfigResult<T> {
   // Layer 1: Start with legacy (lowest precedence)
   let effective: Record<string, unknown> = ctx.legacy ?? {};
   
   // Layer 2: Merge variant template
   effective = deepMerge(effective, ctx.variantTemplate ?? {});
   
   // Layer 3: Merge deal-type template
   effective = deepMerge(effective, ctx.template ?? {});
   
   // Layer 4: Merge persisted overrides
   effective = deepMerge(effective, ctx.overrides ?? {});
   
   // Layer 5: Merge draft (highest precedence, preview-only)
   const hasDraft = ctx.draft !== null && ctx.draft !== undefined && Object.keys(ctx.draft).length > 0;
   if (hasDraft) {
     effective = deepMerge(effective, ctx.draft ?? {});
   }
   
   // Track sources for top-level keys
   const sources: Record<string, ValueSource> = {};
   for (const key of Object.keys(effective)) {
     const traced = traceValueSource(
       key,
       ctx.draft,
       deepMerge(ctx.overrides ?? {}, ctx.template ?? {}),
       ctx.legacy,
       undefined
     );
     // Adjust source tracking to include template layer
     if (ctx.draft?.[key] !== undefined) {
       sources[key] = "overrides"; // Draft is treated as override for display
     } else if (ctx.overrides?.[key] !== undefined) {
       sources[key] = "overrides";
     } else if (ctx.template?.[key] !== undefined) {
       sources[key] = "template";
     } else if (ctx.variantTemplate?.[key] !== undefined) {
       sources[key] = "template";
     } else if (ctx.legacy?.[key] !== undefined) {
       sources[key] = "legacy";
     } else {
       sources[key] = "default";
     }
   }
   
   return {
     effective: effective as T,
     hasDraft,
     sources,
   };
 }
 
 // ================================================================
 // OVERRIDES-FIRST EDITING (D1)
 // ================================================================
 
 /**
  * Apply an edit to the overrides layer.
  * This is the ONLY place edits should go (D1: Overrides-first).
  * 
  * @param currentOverrides - Current overrides object
  * @param path - JSON path (e.g., "components.ribbon.styling.baseColorHex")
  * @param value - New value to set
  * @returns Updated overrides object
  */
 export function applyOverrideEdit(
   currentOverrides: Record<string, unknown> | null,
   path: string,
   value: unknown
 ): Record<string, unknown> {
   const result = { ...(currentOverrides ?? {}) };
   const parts = path.split(".");
   
   // Build nested structure
   let current: Record<string, unknown> = result;
   for (let i = 0; i < parts.length - 1; i++) {
     const part = parts[i];
     if (!(part in current) || typeof current[part] !== "object" || current[part] === null) {
       current[part] = {};
     } else {
       current[part] = { ...(current[part] as Record<string, unknown>) };
     }
     current = current[part] as Record<string, unknown>;
   }
   
   // Set the value
   const lastPart = parts[parts.length - 1];
   current[lastPart] = value;
   
   return result;
 }
 
 /**
  * Remove an override at a path (reset to template/legacy value).
  * 
  * @param currentOverrides - Current overrides object
  * @param path - JSON path to remove
  * @returns Updated overrides object (sanitized to remove empty parents)
  */
 export function removeOverrideAtPath(
   currentOverrides: Record<string, unknown> | null,
   path: string
 ): Record<string, unknown> {
   if (!currentOverrides) {
     return {};
   }
   
   const result = { ...currentOverrides };
   const parts = path.split(".");
   
   // Navigate to parent and delete the key
   const deleteNested = (obj: Record<string, unknown>, parts: string[]): boolean => {
     if (parts.length === 1) {
       delete obj[parts[0]];
       return true;
     }
     
     const [head, ...rest] = parts;
     if (!(head in obj) || typeof obj[head] !== "object" || obj[head] === null) {
       return false;
     }
     
     obj[head] = { ...(obj[head] as Record<string, unknown>) };
     const deleted = deleteNested(obj[head] as Record<string, unknown>, rest);
     
     // Prune empty parent
     if (deleted && Object.keys(obj[head] as object).length === 0) {
       delete obj[head];
     }
     
     return deleted;
   };
   
   deleteNested(result, parts);
   
   // Final sanitization to remove any remaining empty objects
   return sanitizeOverrides(result) ?? {};
 }
 
 // ================================================================
 // SAVE PREPARATION (D3: Sanitization)
 // ================================================================
 
 /**
  * Prepare overrides for persistence.
  * Applies sanitization to prevent empty stomping objects.
  * 
  * @param overrides - Raw overrides from editor
  * @param baseline - Optional baseline to strip redundant values
  * @returns Sanitized overrides ready for DB write
  */
 export function prepareOverridesForSave(
   overrides: Record<string, unknown> | null,
   baseline?: Record<string, unknown> | null
 ): Record<string, unknown> {
   if (!overrides) {
     return {};
   }
   
   // Sanitize to remove empty objects
   const sanitized = sanitizeOverrides(overrides);
   if (!sanitized) {
     return {};
   }
   
   // Optionally strip redundant values that match baseline
   // (Commented out for now - can be enabled for minimal storage)
   // if (baseline) {
   //   return stripRedundantOverrides(sanitized, baseline) ?? {};
   // }
   
   return sanitized;
 }